/*
 * Copyright (c) 2023. Data Smart Technologies CC
 * https://www.datasmart.co.za
 * View license agreement and terms of use at https://www.datasmart.co.za/legal
 *
 * @author  Juan <juan@datasmart.co.za>
 * @version 1.0
 * @updated 2023-07-11
 */

import React, { useEffect, useState } from "react";
import { fetchRecords, formatCurrency } from "../utils/AppUtil";
import { Link } from "react-router-dom";

type MonthlyContributionsProps = {
    endpoint: string;
}

export const MonthlyContributions: React.FC<MonthlyContributionsProps> = (props) => {
    
    const endpoint = props.endpoint;
    const endpointYearToDate: string = "transactions/summary/yearToDate";
    const endpointAllTime: string = "transactions/summary/allTime";
    const endpointRecent: string = "transactions/recent?count=1";

    type ContributionType = {
        average: number,
        total: number,
        target: number,
        count: number
    }

    type TotalType = {
        total: number
    }

    type RecentType = {
        transactionDateDisplay: string
    }

    const contributionData: ContributionType = {
        average: 0,
        total: 0,
        target: 0,
        count: 0
    }

    const yearToDateData: TotalType = {
        total: 0
    }

    const allTimeData: TotalType = {
        total: 0
    }

    const recentData: RecentType = {
        transactionDateDisplay: ""
    }

    const [contribution, setContribution] = useState<ContributionType>(contributionData);
    const [yearToDate, setYearToDate] = useState<TotalType>(yearToDateData);
    const [allTime, setAllTime] = useState<TotalType>(allTimeData);
    const [recent, setRecent] = useState<RecentType>(recentData);

    const processData = (responseJson: ContributionType) => {
        setContribution(responseJson); 
    }
    const processDataYearToDate = (responseJson: TotalType) => {
        setYearToDate(responseJson);
    }
    const processDataAllTime = (responseJson: TotalType) => {
        setAllTime(responseJson);
    }    
    const processDataRecent = (responseJson: any) => {
        const data: RecentType[] = responseJson;
        setRecent(data[0]);
    }    

    // Do not change this or put any additional code in here without checking with
    // the team first.
    useEffect(() => {

        fetchRecords(endpoint, processData)
        .then(() => {
            fetchRecords(endpointYearToDate, processDataYearToDate)
            .then(() => {
                fetchRecords(endpointAllTime, processDataAllTime)
                .then(() => {
                    fetchRecords(endpointRecent, processDataRecent)
                })
            })
        })
        .catch((error) => {
            console.log("Error : " + error)
        });
    }, []);

    const contributionsPercent = ((contribution.total / 175000) * 100) + "%";
    const contributionsAverage = contribution.average.toFixed(0);

    const contributionsBreakpoints = [
        0,
        formatCurrency(175000 * .2),
        formatCurrency(175000 * .4),
        formatCurrency(175000 * .6),
        formatCurrency(175000 * .8),
        formatCurrency(175000)
    ];

    const contributionsBreakpointsMid = formatCurrency(175000 * .5);

    return (
        <div className="card card-contributions jc-border-12">
            <div className="card-header card-header-bg">
                <p className="jc-txt-heading fs-5">Recent Donations</p>
                <p className="jc-txt-body fs-8">Last updated {recent.transactionDateDisplay}</p>
            </div>

            <div className="card-body">
                <div className="card-item-first">
                    <p id="contributions_current" className="jc-txt-body fs-3 mb-2">R{formatCurrency(contribution.total)}</p>

                    <div className="progress mb-1" role="progressbar">
                        <div className="progress-bar" style={{width: `${contributionsPercent}`}}></div>
                    </div>

                    <div className="d-flex justify-content-between jc-txt-body">
                        <p className="fs-8">R{contributionsBreakpoints[0]}</p>
                        <p className="fs-8 d-sm-block d-md-none">R{contributionsBreakpointsMid}</p>
                        <p className="fs-8 d-none d-md-block">R{contributionsBreakpoints[1]}</p>
                        <p className="fs-8 d-none d-md-block">R{contributionsBreakpoints[2]}</p>
                        <p className="fs-8 d-none d-md-block">R{contributionsBreakpoints[3]}</p>
                        <p className="fs-8 d-none d-md-block">R{contributionsBreakpoints[4]}</p>
                        <p className="fs-8">R{contributionsBreakpoints[5]}</p>
                    </div>
                </div>
                <hr className="card-hr" />

                <div className="card-item d-flex justify-content-between">
                    <p className="jc-txt-body fs-6">Monthly contributions to date:</p>
                    <p id="donation_target" className="jc-txt-body fs-6">R{formatCurrency(contribution.total)}</p>
                </div>
                <hr className="card-hr" />

                <div className="card-item d-flex justify-content-between">
                    <p className="jc-txt-body fs-6">Yearly contributions to date:</p>
                    <p id="donation_target" className="jc-txt-body fs-6">R{formatCurrency(yearToDate.total)}</p>
                </div>
                <hr className="card-hr" />

                <div className="card-item d-flex justify-content-between">
                    <p className="jc-txt-body fs-6">Contributions since inception:</p>
                    <p id="donation_target" className="jc-txt-body fs-6">R{formatCurrency(allTime.total)}</p>
                </div>
                <hr className="card-hr" />

                <div className="card-item d-flex justify-content-between">
                    <p className="jc-txt-body fs-6">Monthly Target:</p>
                    <p id="donation_target" className="jc-txt-body fs-6">R{formatCurrency(175000)}</p>
                </div>
                <hr className="card-hr" />

                <div className="card-item-last d-flex justify-content-between">
                    <p className="jc-txt-body fs-6">Average Donation:</p>
                    {/* <p id="donation_average" className="jc-txt-body fs-6">R{contributionsAverage}</p> */}
                    <p id="donation_average" className="jc-txt-body fs-6">R360</p>
                </div>
            </div>

            <div className="card-footer d-flex justify-content-end">
                <Link to="/donate" className="btn btn-dark jc-txt-body jc-txt-light">Donate</Link>
            </div>
        </div>
    );
}